/**
 * Skills Configuration Management Component
 *
 * Manages the Skills tab content on the Configuration page.
 * - Group selector dropdown populated from group_db
 * - Skills displayed organized by Section → Category hierarchy
 * - Add/edit/delete controls for skills and categories
 * - Total skill count for selected group
 * - Seed button when no skills exist
 * - All interactive elements have aria-label attributes
 * - Keyboard navigable
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.8, 1.9, 9.3, 9.4, 9.5, 9.6, 9.7, 9.8, 9.9, 9.10
 */

import { useCallback, useEffect, useState } from 'react';
import { restClient, Group } from '../../../api/rest-client';
import { Button } from '../../common/Button';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import { Modal } from '../../common/Modal';
import { useUIStore } from '../../../store/uiStore';
import { useSkillsStore } from '../../../store/skillsStore';
import type { SkillConfig, CreateSkillConfigDto, UpdateSkillConfigDto } from '../../../types/skills';
import { ThresholdSliders } from './ThresholdSliders';

const VALID_SECTIONS = [
  'Technical Riding Skills',
  'Mental/Attitude Skills',
  'Fitness/Endurance Skills',
] as const;

/**
 * Organize skills into a Section → Category → Skills hierarchy
 */
function organizeSkills(skills: SkillConfig[]): Map<string, Map<string, SkillConfig[]>> {
  const hierarchy = new Map<string, Map<string, SkillConfig[]>>();

  // Initialize sections in display order
  for (const section of VALID_SECTIONS) {
    hierarchy.set(section, new Map());
  }

  for (const skill of skills) {
    let sectionMap = hierarchy.get(skill.section);
    if (!sectionMap) {
      sectionMap = new Map();
      hierarchy.set(skill.section, sectionMap);
    }
    let categorySkills = sectionMap.get(skill.category);
    if (!categorySkills) {
      categorySkills = [];
      sectionMap.set(skill.category, categorySkills);
    }
    categorySkills.push(skill);
  }

  // Remove empty sections
  for (const [section, categories] of hierarchy) {
    if (categories.size === 0) {
      hierarchy.delete(section);
    }
  }

  return hierarchy;
}

/**
 * Skill Form for creating/editing a skill
 */
interface SkillFormProps {
  skill?: SkillConfig;
  groupName: string;
  existingCategories: string[];
  onSubmit: (data: CreateSkillConfigDto | UpdateSkillConfigDto) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

function SkillForm({ skill, groupName, existingCategories, onSubmit, onCancel, isLoading }: SkillFormProps) {
  const [formData, setFormData] = useState({
    section: skill?.section || VALID_SECTIONS[0],
    category: skill?.category || '',
    skill_name: skill?.skill_name || '',
    description: skill?.description || '',
  });
  const [useNewCategory, setUseNewCategory] = useState(!skill && existingCategories.length === 0);
  const [newCategory, setNewCategory] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.skill_name.trim()) newErrors.skill_name = 'Skill name is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!skill) {
      // Creating new skill
      const category = useNewCategory ? newCategory.trim() : formData.category;
      if (!category) newErrors.category = 'Category is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    if (skill) {
      // Update existing skill
      const updateDto: UpdateSkillConfigDto = {
        skill_id: skill.skill_id,
        group_name: skill.group_name,
        skill_name: formData.skill_name.trim(),
        description: formData.description.trim(),
      };
      await onSubmit(updateDto);
    } else {
      // Create new skill
      const category = useNewCategory ? newCategory.trim() : formData.category;
      const createDto: CreateSkillConfigDto = {
        skill_id: crypto.randomUUID(),
        group_name: groupName,
        section: formData.section,
        category,
        skill_name: formData.skill_name.trim(),
        description: formData.description.trim(),
      };
      await onSubmit(createDto);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!skill && (
        <>
          <div>
            <label htmlFor="section" className="block text-sm font-medium text-gray-700 mb-1">
              Section *
            </label>
            <select
              id="section"
              name="section"
              value={formData.section}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Select skill section"
            >
              {VALID_SECTIONS.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
              Category *
            </label>
            {existingCategories.length > 0 && (
              <div className="mb-2 flex items-center gap-2">
                <label className="flex items-center gap-1 text-sm text-gray-600">
                  <input
                    type="radio"
                    checked={!useNewCategory}
                    onChange={() => setUseNewCategory(false)}
                    aria-label="Use existing category"
                  />
                  Existing
                </label>
                <label className="flex items-center gap-1 text-sm text-gray-600">
                  <input
                    type="radio"
                    checked={useNewCategory}
                    onChange={() => setUseNewCategory(true)}
                    aria-label="Create new category"
                  />
                  New
                </label>
              </div>
            )}
            {useNewCategory || existingCategories.length === 0 ? (
              <input
                type="text"
                id="category"
                value={newCategory}
                onChange={e => {
                  setNewCategory(e.target.value);
                  if (errors.category) setErrors(prev => ({ ...prev, category: '' }));
                }}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.category ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="e.g., Bike Basics"
                aria-label="New category name"
              />
            ) : (
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.category ? 'border-red-500' : 'border-gray-300'
                }`}
                aria-label="Select existing category"
              >
                <option value="">Select a category</option>
                {existingCategories.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            )}
            {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
          </div>
        </>
      )}

      <div>
        <label htmlFor="skill_name" className="block text-sm font-medium text-gray-700 mb-1">
          Skill Name *
        </label>
        <input
          type="text"
          id="skill_name"
          name="skill_name"
          value={formData.skill_name}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.skill_name ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="e.g., Bike Check (ABCDE)"
          aria-label="Skill name"
        />
        {errors.skill_name && <p className="mt-1 text-sm text-red-600">{errors.skill_name}</p>}
      </div>

      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Description *
        </label>
        <textarea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={3}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.description ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Describe what this skill involves"
          aria-label="Skill description"
        />
        {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
      </div>

      <div className="flex gap-3 pt-4">
        <Button type="submit" variant="primary" disabled={isLoading} className="flex-1" aria-label={skill ? 'Update skill' : 'Create skill'}>
          {isLoading ? 'Saving...' : skill ? 'Update' : 'Create'}
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading} aria-label="Cancel">
          Cancel
        </Button>
      </div>
    </form>
  );
}


/**
 * Delete Confirmation Modal for skills and categories
 */
interface DeleteConfirmationProps {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

function DeleteConfirmation({ isOpen, title, message, onConfirm, onCancel, isLoading }: DeleteConfirmationProps) {
  return (
    <Modal isOpen={isOpen} onClose={onCancel} title={title}>
      <div className="space-y-4">
        <p className="text-gray-700" dangerouslySetInnerHTML={{ __html: message }} />
        <p className="text-sm text-red-600">This action cannot be undone.</p>
        <div className="flex gap-3 pt-4">
          <Button variant="danger" onClick={onConfirm} disabled={isLoading} className="flex-1" aria-label="Confirm delete">
            {isLoading ? 'Deleting...' : 'Delete'}
          </Button>
          <Button variant="secondary" onClick={onCancel} disabled={isLoading} aria-label="Cancel delete">
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * SkillsConfiguration Component
 *
 * Self-contained admin component for managing per-group skills configuration.
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.8, 1.9, 9.3, 9.4, 9.5, 9.6, 9.7, 9.8, 9.9, 9.10
 */
export function SkillsConfiguration() {
  const showToast = useUIStore(state => state.showToast);
  const fetchThresholds = useSkillsStore(state => state.fetchThresholds);

  // Data state
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [skills, setSkills] = useState<SkillConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState<SkillConfig | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: 'skill' | 'category';
    skill?: SkillConfig;
    section?: string;
    category?: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Collapsed sections state
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  /**
   * Fetch groups on mount
   */
  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const groupsData = await restClient.getGroups();
      const sorted = [...groupsData].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      setGroups(sorted);
    } catch (err) {
      console.error('Failed to fetch groups:', err);
      setError(err instanceof Error ? err.message : 'Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch skills for selected group
   */
  const fetchSkills = useCallback(async (groupName: string) => {
    if (!groupName) {
      setSkills([]);
      return;
    }
    try {
      setSkillsLoading(true);
      setError(null);
      const data = await restClient.getSkillsConfig(groupName);
      setSkills(data);
    } catch (err) {
      console.error('Failed to fetch skills:', err);
      setError(err instanceof Error ? err.message : 'Failed to load skills');
      showToast('error', 'Failed to load skills configuration');
    } finally {
      setSkillsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchGroups();
    fetchThresholds();
  }, [fetchGroups, fetchThresholds]);

  useEffect(() => {
    if (selectedGroup) {
      fetchSkills(selectedGroup);
    } else {
      setSkills([]);
    }
  }, [selectedGroup, fetchSkills]);

  /**
   * Handle group selection change
   */
  const handleGroupChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedGroup(e.target.value);
    setCollapsedSections(new Set());
  };

  /**
   * Toggle section collapse
   */
  const toggleSection = (sectionKey: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  };

  /**
   * Handle add skill
   */
  const handleAddSkill = () => {
    setEditingSkill(undefined);
    setIsFormModalOpen(true);
  };

  /**
   * Handle edit skill
   */
  const handleEditSkill = (skill: SkillConfig) => {
    setEditingSkill(skill);
    setIsFormModalOpen(true);
  };

  /**
   * Handle delete skill
   */
  const handleDeleteSkill = (skill: SkillConfig) => {
    setDeleteTarget({ type: 'skill', skill });
    setIsDeleteModalOpen(true);
  };

  /**
   * Handle delete category
   */
  const handleDeleteCategory = (section: string, category: string) => {
    setDeleteTarget({ type: 'category', section, category });
    setIsDeleteModalOpen(true);
  };

  /**
   * Submit skill form (create or update)
   */
  const handleFormSubmit = useCallback(async (data: CreateSkillConfigDto | UpdateSkillConfigDto) => {
    setIsSubmitting(true);
    setError(null);

    try {
      if ('section' in data) {
        // Create
        await restClient.createSkillConfig(data as CreateSkillConfigDto);
        showToast('success', 'Skill created successfully');
      } else {
        // Update
        await restClient.updateSkillConfig(data as UpdateSkillConfigDto);
        showToast('success', 'Skill updated successfully');
      }

      await fetchSkills(selectedGroup);
      setIsFormModalOpen(false);
      setEditingSkill(undefined);
    } catch (err) {
      console.error('Failed to save skill:', err);
      const msg = err instanceof Error ? err.message : 'Failed to save skill';
      setError(msg);
      showToast('error', msg);
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedGroup, fetchSkills, showToast]);

  /**
   * Confirm delete
   */
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsSubmitting(true);
    setError(null);

    try {
      if (deleteTarget.type === 'skill' && deleteTarget.skill) {
        await restClient.deleteSkillConfig({
          skill_id: deleteTarget.skill.skill_id,
          group_name: deleteTarget.skill.group_name,
        });
        showToast('success', 'Skill deleted successfully');
      } else if (deleteTarget.type === 'category' && deleteTarget.section && deleteTarget.category) {
        await restClient.deleteSkillCategory({
          group_name: selectedGroup,
          section: deleteTarget.section,
          category: deleteTarget.category,
        });
        showToast('success', 'Category and all its skills deleted successfully');
      }

      await fetchSkills(selectedGroup);
      setIsDeleteModalOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to delete:', err);
      const msg = err instanceof Error ? err.message : 'Failed to delete';
      setError(msg);
      showToast('error', msg);
    } finally {
      setIsSubmitting(false);
    }
  }, [deleteTarget, selectedGroup, fetchSkills, showToast]);

  /**
   * Get existing categories for the selected section in the form
   */
  const getExistingCategories = (): string[] => {
    const categories = new Set<string>();
    for (const skill of skills) {
      categories.add(skill.category);
    }
    return Array.from(categories).sort();
  };

  /**
   * Get delete confirmation message
   */
  const getDeleteMessage = (): string => {
    if (!deleteTarget) return '';
    if (deleteTarget.type === 'skill' && deleteTarget.skill) {
      return `Are you sure you want to delete <strong>${deleteTarget.skill.skill_name}</strong>?`;
    }
    if (deleteTarget.type === 'category') {
      const categorySkillCount = skills.filter(
        s => s.section === deleteTarget.section && s.category === deleteTarget.category
      ).length;
      return `Are you sure you want to delete the category <strong>${deleteTarget.category}</strong> and all <strong>${categorySkillCount}</strong> skill${categorySkillCount !== 1 ? 's' : ''} in it?`;
    }
    return '';
  };

  // Organize skills into hierarchy
  const organized = organizeSkills(skills);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <>
      {/* Section Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-bold text-gray-900">Skills Configuration</h2>
          {selectedGroup && !skillsLoading && (
            <span className="text-sm text-gray-500" aria-label={`Total skills count: ${skills.length}`}>
              {skills.length} skill{skills.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Threshold Sliders - visible regardless of selected group */}
      <ThresholdSliders />

      {/* Add Skill Button - below thresholds */}
      {selectedGroup && (
        <div className="mb-6 flex justify-end">
          <Button variant="primary" onClick={handleAddSkill} aria-label="Add new skill">
            Add Skill
          </Button>
        </div>
      )}

      {/* Group Selector */}
      <div className="mb-6">
        <label htmlFor="group-selector" className="block text-sm font-medium text-gray-700 mb-2">
          Select Group
        </label>
        <select
          id="group-selector"
          value={selectedGroup}
          onChange={handleGroupChange}
          className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          aria-label="Select ride group"
        >
          <option value="">Choose a group...</option>
          {groups.map(g => (
            <option key={g.name} value={g.name}>{g.name}</option>
          ))}
        </select>
      </div>

      {/* Content Area */}
      {!selectedGroup ? (
        <div className="text-center py-12 text-gray-500">
          <p>Select a group to view and manage its skills configuration.</p>
        </div>
      ) : skillsLoading ? (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : skills.length === 0 ? (
        <div className="text-center py-12">
          <h3 className="text-sm font-medium text-gray-900">No skills configured for {selectedGroup}</h3>
          <p className="mt-1 text-sm text-gray-500">
            Add skills manually to get started.
          </p>
          <div className="mt-6">
            <Button variant="primary" onClick={handleAddSkill} aria-label="Add first skill">
              Add Skill
            </Button>
          </div>
        </div>
      ) : (
        /* Skills Hierarchy Display */
        <div className="space-y-6">
          {Array.from(organized.entries()).map(([section, categories]) => {
            const sectionKey = section;
            const isCollapsed = collapsedSections.has(sectionKey);
            const sectionSkillCount = Array.from(categories.values()).reduce((sum, s) => sum + s.length, 0);

            return (
              <div key={section} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(sectionKey)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-primary-50 hover:bg-primary-100 transition-colors text-left"
                  aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${section} section`}
                  aria-expanded={!isCollapsed}
                >
                  <div className="flex items-center gap-2">
                    <svg
                      className={`w-4 h-4 text-gray-500 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <h3 className="text-lg font-semibold text-primary-900">{section}</h3>
                  </div>
                  <span className="text-sm text-gray-500">{sectionSkillCount} skill{sectionSkillCount !== 1 ? 's' : ''}</span>
                </button>

                {/* Categories */}
                {!isCollapsed && (
                  <div className="divide-y divide-gray-100">
                    {Array.from(categories.entries()).map(([category, categorySkills]) => (
                      <div key={`${section}-${category}`} className="px-4 py-3">
                        {/* Category Header */}
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                            {category}
                            <span className="ml-2 text-xs font-normal text-gray-400 normal-case tracking-normal">
                              ({categorySkills.length})
                            </span>
                          </h4>
                          <button
                            onClick={() => handleDeleteCategory(section, category)}
                            className="text-xs text-red-500 hover:text-red-700 transition-colors px-2 py-1 rounded hover:bg-red-50"
                            aria-label={`Delete category ${category}`}
                          >
                            Delete Category
                          </button>
                        </div>

                        {/* Skills List */}
                        <div className="space-y-2">
                          {categorySkills.map(skill => (
                            <div
                              key={skill.skill_id}
                              className="flex items-start justify-between p-3 bg-gray-50 rounded-md group hover:bg-gray-100 transition-colors"
                            >
                              <div className="flex-1 min-w-0 mr-3">
                                <p className="text-sm font-medium text-gray-900">{skill.skill_name}</p>
                                <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{skill.description}</p>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <button
                                  onClick={() => handleEditSkill(skill)}
                                  className="text-xs text-primary-600 hover:text-primary-800 transition-colors px-2 py-1 rounded hover:bg-primary-50"
                                  aria-label={`Edit skill ${skill.skill_name}`}
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteSkill(skill)}
                                  className="text-xs text-red-500 hover:text-red-700 transition-colors px-2 py-1 rounded hover:bg-red-50"
                                  aria-label={`Delete skill ${skill.skill_name}`}
                                >
                                  Delete
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Skill Form Modal */}
      <Modal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingSkill(undefined);
        }}
        title={editingSkill ? 'Edit Skill' : 'Add Skill'}
      >
        <SkillForm
          skill={editingSkill}
          groupName={selectedGroup}
          existingCategories={getExistingCategories()}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setIsFormModalOpen(false);
            setEditingSkill(undefined);
          }}
          isLoading={isSubmitting}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmation
        isOpen={isDeleteModalOpen}
        title={deleteTarget?.type === 'category' ? 'Delete Category' : 'Delete Skill'}
        message={getDeleteMessage()}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setDeleteTarget(null);
        }}
        isLoading={isSubmitting}
      />
    </>
  );
}
